import * as i0 from '@angular/core';
import { Directive, Input, HostBinding, Injectable, EventEmitter, Output, ElementRef, Component, ContentChildren, ViewChild, ContentChild, NgModule } from '@angular/core';
import { takeUntil, filter } from 'rxjs/operators';
import { Subject, fromEvent } from 'rxjs';
import * as i2 from '@angular/common';
import { CommonModule } from '@angular/common';
import { __awaiter } from 'tslib';

/**
 * registers item as responsive menu item, all other content will not rendered
 *
 * @example
 *
 * <ngx-responsivemenu>
 *     <button type="button" ngxResponsiveMenuItem>Button 1</button>
 *     <button type="button" ngxResponsiveMenuItem>Button 2</button>
 *     <button type="button" ngxResponsiveMenuItem>Button 3</button>
 *     <!-- this button will not rendered anymore since ngxResponsiveMenuItem is missing -->
 *     <button type="button">Not rendering</button>
 * </ngx-responsivemenu>
 */
class AiHubFlexlistItemDirective {
    constructor(el, renderer) {
        this.el = el;
        this.renderer = renderer;
        /**
         * if set to false menu item will allways rendered to overflow
         * default false
         */
        this.visible = true;
        this.className = true;
    }
    ngOnDestroy() {
        this.remove();
        this.el = null;
    }
    /**
     * add item to parent element
     *
     * @example
     *
     * const parent: ElementRef = this.el;
     * const item: MenuItemDirective = this.menuItem;
     * item.addTo(this.el.nativeElement);
     */
    addTo(parent, before = null) {
        if (before) {
            this.renderer.insertBefore(parent, this.el.nativeElement, before);
            return;
        }
        this.renderer.appendChild(parent, this.el.nativeElement);
    }
    /**
     * remove item from parent children
     *
     * @example
     *
     * const item: MenuItemDirective = this.menuItem;
     * item.remove();
     */
    remove() {
        const root = this.renderer.parentNode(this.el.nativeElement);
        this.renderer.removeChild(root, this.el.nativeElement);
    }
}
AiHubFlexlistItemDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: AiHubFlexlistItemDirective, deps: [{ token: i0.ElementRef }, { token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Directive });
AiHubFlexlistItemDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.3.11", type: AiHubFlexlistItemDirective, selector: "[aihubFlexlistItem]", inputs: { visible: "visible" }, host: { properties: { "class.responsive-menu--item": "this.className" } }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: AiHubFlexlistItemDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[aihubFlexlistItem]'
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: i0.Renderer2 }]; }, propDecorators: { visible: [{
                type: Input
            }], className: [{
                type: HostBinding,
                args: ['class.responsive-menu--item']
            }] } });

/**
 * holds informations which are used from overflow control
 */
class OverflowModel {
    constructor() {
        /**
         * hold all overflow items
         */
        this.overflowItems = [];
    }
    /**
     * set items which should rendered to overflow
     */
    set items(items) {
        this.overflowItems = items;
    }
    /**
     * get items which should rendered to overflow
     */
    get items() {
        return this.overflowItems;
    }
    /**
     * returns true if overflow is not empty
     */
    isOverflow() {
        return this.items.length > 0;
    }
}

/**
 * overflow control to open / close overflow.
 *
 * Every ResponsiveMenu will use this shared service. If you want display multiple
 * responsive menu components u should provide an own OverflowControl service in dependcy injection tree.
 * In this case both components got his own OverflowControl and dont affect each other.
 *
 * @example
 *
 * Component({
 *     selector: "app-sub1-component",
 *     templateUrl: "sub_1.component.html",
 *     styleUrls: ["./sub_1.component.scss"],
 *     viewProviders: [OverflowControl]
 * })
 * export class SubComponent1 implements AfterViewInit, AfterContentInit, OnDestroy {
 *     ...
 * }
 *
 * Component({
 *     selector: "app-sub2-component",
 *     templateUrl: "sub_2.component.html",
 *     styleUrls: ["./sub_2.component.scss"],
 *     viewProviders: [OverflowControl]
 * })
 * export class SubComponent2 implements AfterViewInit, AfterContentInit, OnDestroy {
 *     ...
 * }
 */
class FlexlistOverflowControl {
    constructor() {
        this.rendered = false;
        /**
         * notify all observers if overflow should be rendered
         * but only if overflow items exists
         */
        this.show$ = new Subject();
        /**
         * notify all observers if overflow should be removed
         */
        this.hide$ = new Subject();
        this.overflowModel = new OverflowModel();
    }
    /**
     * set force overflow, if set to true this will allways emits show
     * for overflow conainer even if no overflow items exits.
     *
     * default is set to false
     */
    set forceOverflow(forced) {
        this.forced = forced;
    }
    /**
     * returns oberservable to get notified overflow should be shown
     */
    get show() {
        return this.show$.asObservable();
    }
    /**
     * returns oberservable to get notified overflow should be hide
     */
    get hide() {
        return this.hide$.asObservable();
    }
    /**
     * returns overflow data model
     */
    get data() {
        return this.overflowModel;
    }
    /**
     * retursn true if overflow container is rendered
     */
    isOpen() {
        return this.rendered;
    }
    /**
     * update overflow visibility, if no overflow items exists it will
     * close overflow automatically, unless overflow is forced
     */
    update() {
        if (this.rendered) {
            this.data.items.length === 0 && !this.forced
                ? this.close()
                : this.show$.next(this.data.items);
        }
    }
    /**
     * show overflow content
     */
    open() {
        if (!this.rendered && (this.forced || this.data.items.length)) {
            this.rendered = true;
            this.show$.next(this.data.items);
        }
    }
    /**
     * close overflow content
     */
    close() {
        if (this.rendered) {
            this.rendered = false;
            this.hide$.next(this.data.items);
        }
    }
}
FlexlistOverflowControl.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: FlexlistOverflowControl, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
FlexlistOverflowControl.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: FlexlistOverflowControl });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: FlexlistOverflowControl, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return []; } });

/**
 * toggle button which automatically registers on click event an show overflow if required
 *
 * @example
 *
 * <ngx-responsivemenu>
 *     ...
 *     <button ngxResponsiveMenuToggle>Custom Toggle Button</button>
 * </ngx-responsivemenu>
 */
class AiHubFlexlistToggleDirective {
    /**
     * Creates an instance of MenuToggleDirective.
     */
    constructor(overflowCtrl, renderer, el) {
        this.overflowCtrl = overflowCtrl;
        this.renderer = renderer;
        this.el = el;
        /**
         * add host css class <b>more</b>
         */
        this.className = true;
        this.isDestroyed = new Subject();
    }
    /**
     * display button if soft is true button has css property visibilty: hidden
     */
    set display(display) {
        this.renderer.setStyle(this.el.nativeElement, 'display', display ? null : 'none');
    }
    /**
     * return native element from more button
     */
    get nativeElement() {
        return this.el.nativeElement;
    }
    /**
     * @inheritdoc
     * @ignore
     */
    ngAfterViewInit() {
        fromEvent(this.el.nativeElement, 'click')
            .pipe(takeUntil(this.isDestroyed))
            .subscribe(() => this.openOverflow());
    }
    /**
     * @inheritdoc
     * @ignore
     */
    ngOnDestroy() {
        this.isDestroyed.next(true);
        this.isDestroyed.complete();
    }
    /**
     * get current width of button includes margin
     */
    get width() {
        const width = this.el.nativeElement.getBoundingClientRect().width;
        const style = getComputedStyle(this.el.nativeElement);
        const marginLeft = parseInt(style.getPropertyValue('margin-left'), 10);
        const marginRight = parseInt(style.getPropertyValue('margin-right'), 10);
        return width + marginLeft + marginRight;
    }
    /**
     * if we click on the button we want to open the overlay
     */
    openOverflow() {
        this.overflowCtrl.isOpen() ? this.overflowCtrl.close() : this.overflowCtrl.open();
    }
}
AiHubFlexlistToggleDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: AiHubFlexlistToggleDirective, deps: [{ token: FlexlistOverflowControl }, { token: i0.Renderer2 }, { token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive });
AiHubFlexlistToggleDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.3.11", type: AiHubFlexlistToggleDirective, selector: "[aihubFlexlistToggle]", host: { properties: { "class.toggle": "this.className" } }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: AiHubFlexlistToggleDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[aihubFlexlistToggle]'
                }]
        }], ctorParameters: function () { return [{ type: FlexlistOverflowControl }, { type: i0.Renderer2 }, { type: i0.ElementRef }]; }, propDecorators: { className: [{
                type: HostBinding,
                args: ['class.toggle']
            }] } });

/**
 * AsyncEvent which will emits through EventEmitter
 * so we can do some (async) operations before we
 * let pass / cancel the event.
 *
 */
class AsyncEvent {
    constructor() {
        this.event$ = new Subject();
    }
    /**
     * returns promise which holds current state from event
     * @internal
     */
    get completed() {
        return this.event$.toPromise();
    }
    /**
     * should be called to continue event
     *
     * @example
     *
     * public async onAsyncHook(event: AsyncEvent) {
     *     await this.doAsyncOperation();
     *     event.done();
     * }
     */
    done() {
        this.event$.next(true);
        this.complete();
    }
    /**
     * should be called to continue event
     *
     * @example
     *
     * public async onAsyncHook(event: AsyncEvent) {
     *     try {
     *         await this.doAsyncOperation();
     *         event.done();
     *     } catch (fatalError: Error) {
     *         event.cancel();
     *     }
     * }
     */
    cancel() {
        this.event$.next(false);
        this.complete();
    }
    complete() {
        this.event$.complete();
    }
}

/**
 * renders overflow content if overflow items exists
 *
 * @example
 *
 * <div class="menu">
 *   <!-- append option [customOverflow]=true so default overflow container will not rendered anymore -->
 *   <ngx-responsivemenu [customOverflow]="true">
 *     <button ngxResponsiveMenuItem *ngFor="let btn of buttons"></button>
 *   </ngx-responsivemenu>
 * </div>
 *
 * <div class="sidebar">
 *   <!-- overflow content will rendered here -->
 *   <ngx-responsivemenu-overflow [...EVENTS]></ngx-responsivemenu-overflow>
 * </div>
 */
class AiHubFlexlistOverflowDirective {
    constructor(el, overflowCtrl, renderer) {
        this.el = el;
        this.overflowCtrl = overflowCtrl;
        this.renderer = renderer;
        /**
         * triggers allways after rendering has been completed even if
         * beforeRender / beforeRemove canceled
         */
        this.finalizeRender = new EventEmitter();
        /**
         * overflow container render hook before content will be applied.
         * Emits AsyncEvent which should notfied with $event.done() after
         * all operations are completed
         */
        this.beforeRender = new EventEmitter();
        /**
         * overflow container render hook after content has been applied.
         */
        this.afterRender = new EventEmitter();
        /**
         * overflow container render hook before content will be removed.
         * Emits AsyncEvent which should notfied with $event.done() after
         * all operations are completed
         */
        this.beforeRemove = new EventEmitter();
        /**
         * overflow container render hook after content has been removed.
         */
        this.afterRemove = new EventEmitter();
        this.isDestroyed = new Subject();
    }
    /**
     * @ignore
     */
    ngOnDestroy() {
        this.isDestroyed.next(true);
        this.isDestroyed.complete();
    }
    /**
     * component initialized, if overflow is allready flag as open
     * render directly.
     * Register to show / hide
     *
     * @ignore
     */
    ngOnInit() {
        if (this.overflowCtrl.isOpen()) {
            this.renderContent(this.overflowCtrl.data.items);
        }
        this.registerShowEvent();
        this.registerHideEvent();
    }
    /**
     * subscribe to overflowctrl show observeable
     * only renders content if items exists
     */
    registerShowEvent() {
        this.overflowCtrl.show.pipe(takeUntil(this.isDestroyed), filter((items) => items.length > 0)).subscribe((items) => this.renderContent(items));
    }
    /**
     * subscribe to overflowctrl hide observeable
     * remove content from directive
     */
    registerHideEvent() {
        this.overflowCtrl.hide
            .pipe(takeUntil(this.isDestroyed))
            .subscribe((items) => this.removeContent(items));
    }
    /**
     * render nodes into host view, calls beforeRender and afterRender hooks
     */
    renderContent(nodes) {
        return __awaiter(this, void 0, void 0, function* () {
            let completed = true;
            if (this.beforeRender.observers.length) {
                const event = new AsyncEvent();
                this.beforeRender.emit(event);
                completed = yield event.completed;
            }
            /** add content here */
            if (completed) {
                nodes.forEach((item) => item.addTo(this.el.nativeElement));
                this.renderer.setStyle(this.el.nativeElement, 'display', null);
                this.afterRender.emit();
            }
            this.finalizeRender.emit(completed);
        });
    }
    /**
     * remove nodes from host view, calls beforeRemove, afterRemove
     */
    removeContent(nodes) {
        return __awaiter(this, void 0, void 0, function* () {
            let completed = true;
            if (this.beforeRemove.observers.length) {
                const event = new AsyncEvent();
                this.beforeRemove.emit(event);
                completed = yield event.completed;
            }
            if (completed) {
                nodes.forEach((item) => item.remove());
                this.renderer.setStyle(this.el.nativeElement, 'display', 'none');
                this.afterRemove.emit();
            }
            this.finalizeRender.emit(completed);
        });
    }
}
AiHubFlexlistOverflowDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: AiHubFlexlistOverflowDirective, deps: [{ token: i0.ElementRef }, { token: FlexlistOverflowControl }, { token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Directive });
AiHubFlexlistOverflowDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.3.11", type: AiHubFlexlistOverflowDirective, selector: "[aihubFlexlistOverflow]", outputs: { finalizeRender: "finalizeRender", beforeRender: "beforeRender", afterRender: "afterRender", beforeRemove: "beforeRemove", afterRemove: "afterRemove" }, exportAs: ["flexlistOverflowContent"], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: AiHubFlexlistOverflowDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[aihubFlexlistOverflow]',
                    exportAs: 'flexlistOverflowContent'
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: FlexlistOverflowControl }, { type: i0.Renderer2 }]; }, propDecorators: { finalizeRender: [{
                type: Output
            }], beforeRender: [{
                type: Output
            }], afterRender: [{
                type: Output
            }], beforeRemove: [{
                type: Output
            }], afterRemove: [{
                type: Output
            }] } });

/**
 * possible toggle button alignments
 */
var BtnAlign;
(function (BtnAlign) {
    BtnAlign["LEFT"] = "left";
    BtnAlign["RIGHT"] = "right";
})(BtnAlign || (BtnAlign = {}));
/**
 * Responsive menu component, all items which are passed should be from type
 * ResponsiveMenuItem or ResponsiveMenuToggle. All other items will never rendered
 * into dom
 *
 * @example
 * <ngx-responsivemenu>
 *     <button type="button" ngxResponsiveMenuItem ...>Btn</button>
 * </ngx-responsivemenu>
 */
class AiHubFlexlistComponent {
    /**
     * Creates an instance of ResponsiveMenuComponent.
     */
    constructor(overflowCtrl, renderer, hostEl, changeDetector) {
        this.overflowCtrl = overflowCtrl;
        this.renderer = renderer;
        this.hostEl = hostEl;
        this.changeDetector = changeDetector;
        /**
         * if true default toggle button will not rendered anymore, will be set if a custom item
         * has been added to content from type MenuToggleDirective
         *
         * @ignore
         * @example
         * <ngx-responsivemenu>
         *     ...
         *     <button type="button" ngxResponsiveMenuToggle>Button</div>
         * </ngx-responsivemenu>
         */
        this.isCustomButton = false;
        /**
         * overflow css classes
         *
         * @ignore
         */
        this.overflowClasses = { overflow: true };
        /**
         * set maximal amount of items which could rendered into button pane
         * all other items will automatically add to overflow container
         */
        this.showMax = -1;
        /**
         * if true content will not rendered longer in default container for overflow
         * content and should rendered in a custom overflow container.
         *
         * @example
         *
         * <ngx-responsive-menu [customOverflow]="true">
         *   <button type="button" ngxResponsiveMenuItem class="btn btn-sm btn-secondary" *ngFor="let item of items">{{label}}</button>
         * </ngx-responsive-menu>
         *
         * <div class="superAwesomeOverflow">
         *    <!-- overflow will rendered here now -->
         *    <ngx-responsivemenu-overflow></ngx-responsivemenu-overflow>
         * </div>
         */
        this.customOverflow = false;
        /**
         * set position of toggle btn, possible values are left or right
         */
        this.alignToggle = BtnAlign.RIGHT;
        /**
         * emits if responsive menu has been completed rendering process
         */
        this.rendered = new EventEmitter();
        /**
         * if true toggle button will allways included to button pane and be visible
         */
        this.isForcedOverflow = false;
        /**
         * emits true if component gets destroyed
         */
        this.isDestroyed$ = new Subject();
        /**
         * possible overflow items, which fits into button bar but not with more button
         * but since the next button could be the last button and be smaller then overflow button
         * which could results into that all buttons fits into the bar we only have to mark this button
         * for an overflow button
         */
        this.possibleOverflowItems = [];
        /**
         * all overflow items which exists
         */
        this.overflowItems = [];
    }
    /**
     * if true toggle button will allways be visible even if content
     * fits into button pane
     */
    set forceOverflow(forced) {
        this.isForcedOverflow = forced;
        this.overflowCtrl.forceOverflow = forced;
    }
    /**
     * add class to overflow container, only for default overflow if option customOverflow is passed
     * this class will not added to custom overflow, simply use [ngClass]="'className'" if you want to add
     * a custom class
     *
     * @example
     * <ngx-responsive-menu [classOverflow]="'overflow-container'">
     *    ...
     * </ngx-responsive-menu>
     */
    set classOverflow(name) {
        this.overflowClasses[name] = true;
    }
    /**
     * static: false wait until change detection loop has been finished in this case
     * button el will not rendered to dom if a custom button is given but we have to wait
     * until change detection finished before we get it
     */
    set defaultToggleBtn(btn) {
        if (!this.toggleBtn) {
            this.toggleBtn = btn;
        }
    }
    /**
     * check if custom button is defined so we dont need to render default more button
     */
    set customToggleButton(btn) {
        this.isCustomButton = Boolean(btn);
        if (btn) {
            this.toggleBtn = btn;
        }
    }
    /**
     * component gets destroyed
     */
    ngOnDestroy() {
        this.isDestroyed$.next(true);
    }
    /**
     * after content has initialized register to QueryList
     * to get notified about changes
     */
    ngAfterContentInit() {
        this.menuItems.changes
            .pipe(takeUntil(this.isDestroyed$))
            .subscribe(() => this.update());
    }
    /**
     * after view has been initialized and custom button exists
     * append custom toggle button to button pane
     */
    ngAfterViewInit() {
        if (this.isCustomButton) {
            this.renderer.appendChild(this.buttonPane.nativeElement, this.toggleBtn.nativeElement);
        }
        this.render();
    }
    /**
     * update view, this will remove all
     * contents and rerender buttons
     */
    update(width) {
        this.render(width);
        this.overflowCtrl.update();
    }
    /**
     * remove old items from view so we ensure we have a clean tree
     */
    clearView() {
        this.menuItems.forEach((menuItem) => {
            menuItem.remove();
        });
        this.changeDetector.detectChanges();
    }
    /**
     * render buttons to button menubar, if they not fits anymore or
     * max show count is reached put them directly to the overflow container
     */
    render(width) {
        this.initRenderProcess();
        this.maxWidth = width || this.calcHostWidth();
        const items = this.prepareMenuItems();
        let isOverflow = false;
        for (let index = 0, count = 0, ln = items.length; index < ln; index++, count++) {
            const item = items[index];
            isOverflow = isOverflow || this.showMax > -1 && count >= this.showMax;
            if (!isOverflow) {
                item.addTo(this.tmpButtonPane.nativeElement);
                if (this.validateSize(item)) {
                    continue;
                }
                isOverflow = true;
            }
            this.overflowItems.push(item);
        }
        this.finalizeRenderProcess();
    }
    /**
     * initialize render process
     * clean up all views, get dimensions from elements
     */
    initRenderProcess() {
        this.clearView();
        this.overflowItems = [];
        this.possibleOverflowItems = [];
        this.overflowCtrl.data.items = [];
        this.toggleBtn.display = true;
        this.reservedWidth = this.toggleBtn.width;
        this.toggleBtn.display = false;
    }
    /**
     * finialize render process, enable more button if an overflow exists
     */
    finalizeRenderProcess() {
        const overflowData = this.finalizeMenuItems();
        this.overflowCtrl.data.items = overflowData;
        this.toggleBtn.display = this.isForcedOverflow || overflowData.length > 0;
        this.possibleOverflowItems = [];
        this.overflowItems = [];
        this.changeDetector.detectChanges();
        this.rendered.emit();
    }
    /**
     * prepare menu items, filter out items which should be hidden by default
     * and put them to overflow
     */
    prepareMenuItems() {
        return this.menuItems.reduce((itemCollection, menuItem) => {
            menuItem.visible
                ? itemCollection.push(menuItem)
                : this.overflowItems.push(menuItem);
            return itemCollection;
        }, []);
    }
    /**
     * finalize menu buttons after render process finished
     * buttons which are in overflow will removed from dom and pushed to overflow array
     * all others will added to buttonPane
     */
    finalizeMenuItems() {
        const items = this.overflowItems.length
            ? this.possibleOverflowItems.concat(this.overflowItems)
            : [];
        return this.menuItems.toArray().reduce((overflowItems, item) => {
            // remove all items so they are not rendered anymore
            item.remove();
            if (!items.length || items.indexOf(item) === -1) {
                this.alignToggle === BtnAlign.LEFT
                    ? item.addTo(this.buttonPane.nativeElement)
                    : item.addTo(this.buttonPane.nativeElement, this.toggleBtn.nativeElement);
                return overflowItems;
            }
            /** push item to overflow */
            overflowItems.push(item);
            return overflowItems;
        }, []);
    }
    /**
     * validate rendered item fits into button container
     */
    validateSize(item) {
        const usedSize = parseInt(this.tmpButtonPane.nativeElement.offsetWidth, 10);
        /** item fits together with more button */
        if (usedSize + this.reservedWidth <= this.maxWidth) {
            return true;
        }
        /**
         * menu item dosent fit into pane with toggle button
         * but since this could be the last item, or the next item
         * which come is smaller as the toggle button we push this
         * into possible overflow items, unless toggle button should shown
         * allways
         */
        if (usedSize <= this.maxWidth && !this.isForcedOverflow) {
            this.possibleOverflowItems.push(item);
            return true;
        }
        /**
         * item dont fits anymore this is an overflow
         */
        return false;
    }
    /**
     * calculate inner width from host element
     */
    calcHostWidth() {
        const hostNode = this.hostEl.nativeElement;
        const hostStyle = getComputedStyle(hostNode);
        let width = Math.floor(hostNode.getBoundingClientRect().width);
        /** @todo check better solution to not calculate this every time */
        width += parseInt(hostStyle.getPropertyValue('border-left-width'), 10);
        width += parseInt(hostStyle.getPropertyValue('border-right-width'), 10);
        width += parseInt(hostStyle.getPropertyValue('padding-right'), 10);
        width += parseInt(hostStyle.getPropertyValue('padding-left'), 10);
        return width;
    }
}
AiHubFlexlistComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: AiHubFlexlistComponent, deps: [{ token: FlexlistOverflowControl }, { token: i0.Renderer2 }, { token: i0.ElementRef }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
AiHubFlexlistComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.3.11", type: AiHubFlexlistComponent, selector: "aihub-flexlist", inputs: { showMax: "showMax", customOverflow: "customOverflow", forceOverflow: "forceOverflow", classBtnPane: "classBtnPane", classOverflow: "classOverflow", alignToggle: "alignToggle" }, outputs: { rendered: "rendered" }, queries: [{ propertyName: "customToggleButton", first: true, predicate: AiHubFlexlistToggleDirective, descendants: true, read: AiHubFlexlistToggleDirective, static: true }, { propertyName: "menuItems", predicate: AiHubFlexlistItemDirective }], viewQueries: [{ propertyName: "defaultToggleBtn", first: true, predicate: AiHubFlexlistToggleDirective, descendants: true, read: AiHubFlexlistToggleDirective }, { propertyName: "buttonPane", first: true, predicate: ["buttonPane"], descendants: true, read: ElementRef, static: true }, { propertyName: "tmpButtonPane", first: true, predicate: ["tmpButtonPane"], descendants: true, read: ElementRef, static: true }], ngImport: i0, template: "<!-- visible buttons -->\n<div class=\"buttonWrapper\">\n    <!-- should use tmp button pane to render buttons first -->\n    <div #tmpButtonPane class=\"buttonPane\" [ngClass]=\"classBtnPane\"></div>\n    <div #buttonPane class=\"buttonPane\" [ngClass]=\"classBtnPane\">\n        <button type=\"button\"  aihubFlexlistToggle *ngIf=\"!isCustomButton\">...</button>\n    </div>\n</div>\n\n<!-- default container where over items will be rendered in -->\n<div aihubFlexlistOverflow *ngIf=\"!customOverflow\" [ngClass]=\"overflowClasses\"></div>", styles: [":host{display:flex;flex-direction:column;justify-content:center}:host .buttonWrapper{display:flex;flex-direction:row}:host .buttonPane{display:inline-flex;flex-direction:row;flex-wrap:nowrap;flex-shrink:0}:host ::ng-deep .responsive-menu--item{flex-shrink:0!important}:host-context(.flexlist-left) .buttonWrapper{justify-content:flex-end}\n"], directives: [{ type: i2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { type: AiHubFlexlistToggleDirective, selector: "[aihubFlexlistToggle]" }, { type: AiHubFlexlistOverflowDirective, selector: "[aihubFlexlistOverflow]", outputs: ["finalizeRender", "beforeRender", "afterRender", "beforeRemove", "afterRemove"], exportAs: ["flexlistOverflowContent"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: AiHubFlexlistComponent, decorators: [{
            type: Component,
            args: [{ selector: 'aihub-flexlist', template: "<!-- visible buttons -->\n<div class=\"buttonWrapper\">\n    <!-- should use tmp button pane to render buttons first -->\n    <div #tmpButtonPane class=\"buttonPane\" [ngClass]=\"classBtnPane\"></div>\n    <div #buttonPane class=\"buttonPane\" [ngClass]=\"classBtnPane\">\n        <button type=\"button\"  aihubFlexlistToggle *ngIf=\"!isCustomButton\">...</button>\n    </div>\n</div>\n\n<!-- default container where over items will be rendered in -->\n<div aihubFlexlistOverflow *ngIf=\"!customOverflow\" [ngClass]=\"overflowClasses\"></div>", styles: [":host{display:flex;flex-direction:column;justify-content:center}:host .buttonWrapper{display:flex;flex-direction:row}:host .buttonPane{display:inline-flex;flex-direction:row;flex-wrap:nowrap;flex-shrink:0}:host ::ng-deep .responsive-menu--item{flex-shrink:0!important}:host-context(.flexlist-left) .buttonWrapper{justify-content:flex-end}\n"] }]
        }], ctorParameters: function () { return [{ type: FlexlistOverflowControl }, { type: i0.Renderer2 }, { type: i0.ElementRef }, { type: i0.ChangeDetectorRef }]; }, propDecorators: { menuItems: [{
                type: ContentChildren,
                args: [AiHubFlexlistItemDirective]
            }], showMax: [{
                type: Input
            }], customOverflow: [{
                type: Input
            }], forceOverflow: [{
                type: Input
            }], classBtnPane: [{
                type: Input
            }], classOverflow: [{
                type: Input
            }], alignToggle: [{
                type: Input
            }], rendered: [{
                type: Output
            }], defaultToggleBtn: [{
                type: ViewChild,
                args: [AiHubFlexlistToggleDirective, { read: AiHubFlexlistToggleDirective, static: false }]
            }], customToggleButton: [{
                type: ContentChild,
                args: [AiHubFlexlistToggleDirective, { read: AiHubFlexlistToggleDirective, static: true }]
            }], buttonPane: [{
                type: ViewChild,
                args: ['buttonPane', { read: ElementRef, static: true }]
            }], tmpButtonPane: [{
                type: ViewChild,
                args: ['tmpButtonPane', { read: ElementRef, static: true }]
            }] } });

class AiHubFlexListModule {
}
AiHubFlexListModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: AiHubFlexListModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
AiHubFlexListModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: AiHubFlexListModule, declarations: [AiHubFlexlistItemDirective,
        AiHubFlexlistToggleDirective,
        AiHubFlexlistOverflowDirective,
        AiHubFlexlistComponent], imports: [CommonModule], exports: [AiHubFlexlistItemDirective,
        AiHubFlexlistToggleDirective,
        AiHubFlexlistOverflowDirective,
        AiHubFlexlistComponent] });
AiHubFlexListModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: AiHubFlexListModule, providers: [
        FlexlistOverflowControl
    ], imports: [[
            CommonModule
        ]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: AiHubFlexListModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule
                    ],
                    exports: [
                        AiHubFlexlistItemDirective,
                        AiHubFlexlistToggleDirective,
                        AiHubFlexlistOverflowDirective,
                        AiHubFlexlistComponent
                    ],
                    providers: [
                        FlexlistOverflowControl
                    ],
                    declarations: [
                        AiHubFlexlistItemDirective,
                        AiHubFlexlistToggleDirective,
                        AiHubFlexlistOverflowDirective,
                        AiHubFlexlistComponent
                    ]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { AiHubFlexListModule, AiHubFlexlistComponent, AiHubFlexlistItemDirective, AiHubFlexlistOverflowDirective, AiHubFlexlistToggleDirective, BtnAlign, FlexlistOverflowControl };
